import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, X, Download } from "lucide-react";
import {
  listTemples,
  createTemple,
  updateTemple,
  deleteTemple,
  type TempleWrite,
} from "@/lib/temples-api";

type Temple = {
  id?: string;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  yatra_order: number;
  mool_nayak: string;
  established_year: number | null;
  latitude: number | null;
  longitude: number | null;
  history_en: string;
  history_hi: string;
  history_gu: string;
  qr_code: string;
  /** Backend-generated QR image URL (read-only). */
  qr_image?: string | null;
  image_url: string | null;
  audio_url: string | null;
  /** New files chosen in the editor (not yet uploaded). */
  imageFile?: File | null;
  audioFile?: File | null;
};

const EMPTY: Temple = {
  name_en: "",
  name_hi: "",
  name_gu: "",
  area: "",
  yatra_order: 1,
  mool_nayak: "",
  established_year: null,
  latitude: null,
  longitude: null,
  history_en: "",
  history_hi: "",
  history_gu: "",
  qr_code: "",
  image_url: null,
  audio_url: null,
};

/** Force-download a (possibly cross-origin) QR image; falls back to opening it if CORS blocks the fetch. */
async function downloadQr(url: string, name: string) {
  const safe = name.trim().replace(/[^\w-]+/g, "_") || "temple";
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(String(res.status));
    }
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `${safe}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export const Route = createFileRoute("/admin/temples")({ component: TemplesAdmin });

function TemplesAdmin() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<Temple | null>(null);
  const [view, setView] = useState<Temple | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-temples"],
    queryFn: listTemples,
  });

  const toWrite = (d: Temple): TempleWrite => ({
    yatra_order: d.yatra_order,
    name_en: d.name_en,
    name_hi: d.name_hi,
    name_gu: d.name_gu,
    area: d.area,
    mool_nayak: d.mool_nayak,
    established_year: d.established_year,
    latitude: d.latitude,
    longitude: d.longitude,
    history_en: d.history_en,
    history_hi: d.history_hi,
    history_gu: d.history_gu,
    imageFile: d.imageFile ?? null,
    audioFile: d.audioFile ?? null,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-temples"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: Temple) => createTemple(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Temple created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: Temple) => updateTemple(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Temple updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTemple(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!edit.imageFile && !edit.image_url) {
      toast.error("Image required");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };

  const remove = (id: string) => {
    if (confirm("Delete this temple?")) {
      deleteMut.mutate(id);
    }
  };

  const filtered = list.filter((t) =>
    (t.name_en + t.area + t.mool_nayak).toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Temples</h2>
          <p className="text-sm text-muted-foreground">
            Manage all Jain Derasars (3 languages, history, QR, location)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary"
            />
          </div>
          <button
            onClick={() =>
              setEdit({
                ...EMPTY,
                yatra_order: list.length + 1,
                qr_code: `JGY-T${String(list.length + 1).padStart(2, "0")}`,
              })
            }
            className="btn-primary py-2 text-sm"
          >
            <Plus className="h-4 w-4" /> Add Temple
          </button>
        </div>
      </div>

      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">#</th>
                <th className="p-3">Name</th>
                <th className="p-3">Area</th>
                <th className="p-3">Mool Nayak</th>
                <th className="p-3">QR</th>
                <th className="p-3">Year</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    Loading temples…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-destructive">
                    {error instanceof Error ? error.message : "Failed to load temples"}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    No temples yet
                  </td>
                </tr>
              )}
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setView(t)}
                  className="cursor-pointer border-t border-border hover:bg-muted/40"
                >
                  <td className="p-3 font-bold text-primary">{t.yatra_order}</td>
                  <td className="p-3">
                    <div className="font-semibold">{t.name_en}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {t.name_gu} · {t.name_hi}
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{t.area}</td>
                  <td className="p-3">{t.mool_nayak}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {t.qr_image ? (
                        <a
                          href={t.qr_image}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img
                            src={t.qr_image}
                            alt={`QR for ${t.name_en}`}
                            className="h-10 w-10 shrink-0 rounded-md border border-border bg-white object-contain"
                          />
                        </a>
                      ) : null}
                      <span className="font-mono text-xs">{t.qr_code}</span>
                      {t.qr_image ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadQr(t.qr_image as string, t.name_en);
                          }}
                          title="Download QR"
                          className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-primary"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{t.established_year ?? "—"}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEdit(t);
                        }}
                        title="Edit"
                        className="rounded-lg p-1.5 hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (t.id) {
                            remove(t.id);
                          }
                        }}
                        title="Delete"
                        className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {view && (
        <TempleDetail
          temple={view}
          onClose={() => setView(null)}
          onEdit={() => {
            setEdit(view);
            setView(null);
          }}
        />
      )}

      {edit && (
        <Editor
          temple={edit}
          setTemple={setEdit}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function TempleDetail({
  temple: t,
  onClose,
  onEdit,
}: {
  temple: Temple;
  onClose: () => void;
  onEdit: () => void;
}) {
  const coords =
    t.latitude !== null && t.longitude !== null ? `${t.latitude}, ${t.longitude}` : "—";
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-3xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              #{t.yatra_order} · Temple Details
            </div>
            <h3 className="font-serif text-xl font-bold">{t.name_en}</h3>
            <div className="text-sm text-muted-foreground">
              {t.name_gu} · {t.name_hi}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {t.image_url ? (
          <img
            src={t.image_url}
            alt={t.name_en}
            className="mb-4 max-h-56 w-full rounded-xl border border-border object-cover"
          />
        ) : null}

        {t.audio_url ? (
          <div className="mb-4">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Audio
            </div>
            <audio src={t.audio_url} controls className="w-full" />
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Detail label="Area" value={t.area} />
          <Detail label="Mool Nayak" value={t.mool_nayak} />
          <Detail label="Established Year" value={t.established_year ?? "—"} />
          <Detail label="Coordinates" value={coords} />
          <Detail label="QR Code" value={<span className="font-mono text-xs">{t.qr_code}</span>} />
          {t.qr_image ? (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                QR Image
              </div>
              <a href={t.qr_image} target="_blank" rel="noopener noreferrer" className="mt-1 block">
                <img
                  src={t.qr_image}
                  alt={`QR for ${t.name_en}`}
                  className="h-20 w-20 rounded-lg border border-border bg-white object-contain"
                />
              </a>
            </div>
          ) : null}
        </div>

        {(t.history_en || t.history_hi || t.history_gu) && (
          <div className="mt-4 space-y-3">
            {[
              { label: "History (English)", value: t.history_en },
              { label: "History (हिन्दी)", value: t.history_hi },
              { label: "History (ગુજરાતી)", value: t.history_gu },
            ]
              .filter((h) => h.value)
              .map((h) => (
                <div key={h.label} className="rounded-xl border border-border bg-muted/40 p-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {h.label}
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{h.value}</p>
                </div>
              ))}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline py-2 text-sm">
            Close
          </button>
          <button onClick={onEdit} className="btn-primary py-2 text-sm">
            <Pencil className="h-4 w-4" /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm">{value || "—"}</div>
    </div>
  );
}

function Editor({
  temple,
  setTemple,
  onSave,
  onClose,
  saving,
}: {
  temple: Temple;
  setTemple: (t: Temple) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const u = (k: keyof Temple, v: any) => setTemple({ ...temple, [k]: v });
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-3xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold">{temple.id ? "Edit" : "Add"} Temple</h3>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Yatra Order">
            <input
              type="number"
              value={temple.yatra_order}
              onChange={(e) => u("yatra_order", +e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="QR Code">
            <input
              value={temple.qr_code}
              onChange={(e) => u("qr_code", e.target.value.toUpperCase())}
              className="inp font-mono"
            />
            {temple.qr_image ? (
              <div className="mt-2 flex items-end gap-3">
                <a href={temple.qr_image} target="_blank" rel="noopener noreferrer">
                  <img
                    src={temple.qr_image}
                    alt={`QR for ${temple.name_en}`}
                    className="h-24 w-24 rounded-lg border border-border bg-white object-contain"
                  />
                </a>
                <button
                  type="button"
                  onClick={() => downloadQr(temple.qr_image as string, temple.name_en)}
                  className="btn-outline py-1.5 text-xs"
                >
                  <Download className="h-3.5 w-3.5" /> Download QR
                </button>
              </div>
            ) : null}
          </Field>
          <Field label="Name (English)">
            <input
              value={temple.name_en}
              onChange={(e) => u("name_en", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="Name (हिन्दी)">
            <input
              value={temple.name_hi}
              onChange={(e) => u("name_hi", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="Name (ગુજરાતી)">
            <input
              value={temple.name_gu}
              onChange={(e) => u("name_gu", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="Area">
            <input
              value={temple.area}
              onChange={(e) => u("area", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="Mool Nayak">
            <input
              value={temple.mool_nayak}
              onChange={(e) => u("mool_nayak", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="Established Year">
            <input
              type="number"
              value={temple.established_year ?? ""}
              onChange={(e) => u("established_year", e.target.value ? +e.target.value : null)}
              className="inp"
            />
          </Field>
          <Field label="Latitude">
            <input
              type="number"
              step="any"
              value={temple.latitude ?? ""}
              onChange={(e) => u("latitude", e.target.value ? +e.target.value : null)}
              className="inp"
            />
          </Field>
          <Field label="Longitude">
            <input
              type="number"
              step="any"
              value={temple.longitude ?? ""}
              onChange={(e) => u("longitude", e.target.value ? +e.target.value : null)}
              className="inp"
            />
          </Field>
          <Field label="Image" full required>
            <input
              type="file"
              accept="image/*"
              required={!temple.imageFile && !temple.image_url}
              onChange={(e) => u("imageFile", e.target.files?.[0] ?? null)}
              className="inp"
            />
            {temple.imageFile ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Selected: {temple.imageFile.name}
              </p>
            ) : temple.image_url ? (
              <img
                src={temple.image_url}
                alt={temple.name_en}
                className="mt-2 h-24 w-auto rounded-lg border border-border object-cover"
              />
            ) : null}
          </Field>
          <Field label="Audio" full>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => u("audioFile", e.target.files?.[0] ?? null)}
              className="inp"
            />
            {temple.audioFile ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Selected: {temple.audioFile.name}
              </p>
            ) : temple.audio_url ? (
              <audio src={temple.audio_url} controls className="mt-2 w-full" />
            ) : null}
          </Field>
          <Field label="History (English)" full>
            <textarea
              rows={3}
              value={temple.history_en}
              onChange={(e) => u("history_en", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="History (हिन्दी)" full>
            <textarea
              rows={3}
              value={temple.history_hi}
              onChange={(e) => u("history_hi", e.target.value)}
              className="inp"
            />
          </Field>
          <Field label="History (ગુજરાતી)" full>
            <textarea
              rows={3}
              value={temple.history_gu}
              onChange={(e) => u("history_gu", e.target.value)}
              className="inp"
            />
          </Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-outline py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
    </div>
  );
}

function Field({
  label,
  full,
  required,
  children,
}: {
  label: string;
  full?: boolean;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
